/**
 * InventoryDynamicPage — 재고관리 (JDBC 동적 SQL — SQL Append + 프로시저).
 *
 * 【역할】
 * - InventoryListPage 의 MyBatis 경로와 달리 JDBC 동적 SQL 경로 (SQL Append) 사용
 * - 확장 필터(minQty/maxQty)를 런타임에 SQL 조각으로 합성
 *
 * 【주요 기능】
 * - 검색 (확장 필터 minQty/maxQty 동적 WHERE 생성)
 * - CRUD (편집 모달 + 벌크 저장)
 * - 창고별 요약 (프로시저)
 * - 수량 조정 (프로시저)
 * - Oracle UPPERCASE 컬럼 → camelCase 키 정규화 (MyBatis 경로와 호환)
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useCodes / useLabel
 *
 * 【Backend RPC serviceName】
 * - inventoryDynamic/searchList  — SQL Append 조회 (minQty/maxQty 확장 필터)
 * - inventoryDynamic/save        — SQL Append CRUD 저장
 * - inventoryDynamic/summary     — 창고별 요약 프로시저
 * - inventoryDynamic/adjust      — 수량 조정 프로시저
 *
 * 【DataSet 구조】
 * - ds_list: 재고 목록 (camelCase 정규화 후)
 * - ds_summary: 창고별 집계
 *
 * 【사용처】 router/index.tsx — `{ path: 'inventory-dynamic' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageInventoryDynamic.vue
 */
import { useEffect, useMemo, useState } from 'react'
import { Form, Input, InputNumber, Select, Modal, Button, Tabs, Space, Flex } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useCodes } from '@/framework/useCodes'
import { useLabel } from '@/framework/useLabel'
import RowStateTag from '@/components/common/RowStateTag'
import AppPage from '@/components/common/AppPage'
import CrudToolbar from '@/components/common/CrudToolbar'
import DataTable from '@/components/common/DataTable'
import FormSelect from '@/components/common/FormSelect'
import FormInput from '@/components/common/FormInput'
import { fmtDate } from '@/utils/format'

/**
 * JDBC 동적 SQL 서비스는 Oracle 컬럼명(UPPERCASE)을 그대로 반환한다.
 * MyBatis 경로(camelCase)와 달라 Table dataIndex 와 불일치하므로, 로드 시
 * 키를 camelCase 로 정규화한다.
 */
const UPPER_TO_CAMEL: Record<string, string> = {
  INVENTORYID: 'inventoryId',
  ITEMCODE: 'itemCode',
  ITEMNAME: 'itemName',
  WAREHOUSECD: 'warehouseCd',
  QUANTITY: 'quantity',
  UNIT: 'unit',
  LASTINBOUNDDATE: 'lastInboundDate',
  NOTE: 'note',
  CREATEDAT: 'createdAt',
  CREATEDBY: 'createdBy',
  UPDATEDAT: 'updatedAt',
  UPDATEDBY: 'updatedBy'
}
const SUMMARY_UPPER_TO_CAMEL: Record<string, string> = {
  WAREHOUSECD: 'warehouseCd',
  ITEMCOUNT: 'itemCount',
  TOTALQTY: 'totalQty',
  LASTINBOUND: 'lastInbound'
}
/**
 * Oracle UPPERCASE 키 → camelCase 키 정규화.
 * 매핑 테이블에 없으면 lowercase 로 fallback (idempotent — 이미 camelCase 면 그대로 유지).
 */
function normalizeKeys<T extends object>(row: unknown, map: Record<string, string>): T {
  // 1) 비객체 방어 (null/undefined/primitive 그대로 통과)
  if (!row || typeof row !== 'object') return row as T
  // 2) entries 순회하며 키 변환 — UPPER 매핑 우선, 없으면 lowercase
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(row as Record<string, unknown>)) {
    const lower = k.toLowerCase()
    const mapped = map[k.toUpperCase()] ?? lower
    out[mapped] = v
  }
  return out as T
}

interface InventoryRow {
  /** 재고 PK */
  inventoryId?: string
  /** 품목코드 */
  itemCode?: string
  /** 품목명 */
  itemName?: string
  /** 창고 코드 — 공통코드 WAREHOUSE */
  warehouseCd?: string
  /** 수량 — 음수 불가 */
  quantity?: number
  /** 단위 — 공통코드 UNIT */
  unit?: string
  /** 최종입고일 — 'YYYY-MM-DD' (DynamicPage 는 Input 직접 입력) */
  lastInboundDate?: string
  /** 비고 */
  note?: string
}

interface SummaryRow {
  /** 창고 코드 (group by) */
  warehouseCd?: string
  /** 품목 종류 수 */
  itemCount?: number
  /** 총 수량 합계 */
  totalQty?: number
  /** 마지막 입고일 */
  lastInbound?: string
}

/**
 * InventoryDynamicPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function InventoryDynamicPage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { getCodeName, getOptions, loadCodes } = useCodes()
  const { t } = useLabel()

  const ds_list = $createDataSet<InventoryRow>([])
  const ds_summary = $createDataSet<SummaryRow>([])

  const [searchForm] = Form.useForm()
  const [editForm] = Form.useForm<InventoryRow>()
  const [editOpen, setEditOpen] = useState(false)
  const [editMode, setEditMode] = useState<'C' | 'U'>('C')
  const [editingIndex, setEditingIndex] = useState<number>(-1)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [loading, setLoading] = useState(false)

  const [adjustOpen, setAdjustOpen] = useState(false)
  const [adjustTarget, setAdjustTarget] = useState<InventoryRow | null>(null)
  const [adjustDelta, setAdjustDelta] = useState<number>(0)

  useEffect(() => {
    // ─── 공통코드 lazy load (WAREHOUSE/UNIT) ─
    loadCodes(['WAREHOUSE', 'UNIT'])
  }, [loadCodes])

  /** 검색 — JDBC 동적 SQL 호출 + UPPERCASE→camelCase 정규화 후 ds_list 적재. */
  const fn_search = async () => {
    // ─── 검색 + 키 정규화 + load ─
    // 1) 로딩 표시
    setLoading(true)
    // 2) RPC — outds 미사용 (수동 정규화 필요로 res.data 직접 처리)
    const res = await $transaction({
      id: 'select',
      service: 'inventoryDynamic/searchList',
      action: 'search',
      params: searchForm.getFieldsValue()
    })
    setLoading(false)
    if (res.success) {
      // 3) 페이로드 추출 — 백엔드 응답 형태 호환 (ds_inventory / ds_list, 배열 / {rows})
      const payload = res.data?.ds_inventory ?? res.data?.ds_list ?? []
      const rawRows: unknown[] = Array.isArray(payload)
        ? payload
        : typeof payload === 'object' &&
            payload &&
            Array.isArray((payload as { rows?: unknown }).rows)
          ? (payload as { rows: unknown[] }).rows
          : []
      // 4) Oracle UPPERCASE 컬럼명 → camelCase 키 변환 (Table dataIndex 호환)
      const normalized = rawRows.map((r) => normalizeKeys<InventoryRow>(r, UPPER_TO_CAMEL))
      // 5) ds_list.load — 변경상태 모두 'N' 으로 reset
      ds_list.load(normalized)
      setSelectedRowKeys([])
    }
  }

  /** 검색 초기화 — 폼/list/summary/선택 모두 reset. */
  const fn_reset = () => {
    // ─── 폼/2개 DS/선택 초기화 ─
    searchForm.resetFields()
    ds_list.clear()
    ds_summary.clear()
    setSelectedRowKeys([])
  }

  /** 신규 — 편집 모달 C 모드 + 기본값(WH-A/0/EA). */
  const fn_add = () => {
    // ─── 편집 모달 C 모드 진입 ─
    setEditMode('C')
    setEditingIndex(-1)
    editForm.resetFields()
    editForm.setFieldsValue({
      warehouseCd: 'WH-A',
      quantity: 0,
      unit: 'EA'
    })
    setEditOpen(true)
  }

  /** 수정 — 단일 선택 행을 편집 모달 U 모드로 로딩. */
  const fn_edit = () => {
    // ─── 단일 선택 검증 + U 모드 ─
    if (selectedRowKeys.length !== 1) {
      $msg.byId('MSG_SELECT_EDIT_ROW')
      return
    }
    const idx = visibleRows.findIndex((r) => r._rowId === selectedRowKeys[0])
    if (idx < 0) return
    setEditMode('U')
    setEditingIndex(idx)
    // 날짜 변환 불필요 — DynamicPage 는 Input(text) 사용
    editForm.setFieldsValue(visibleRows[idx].data)
    setEditOpen(true)
  }

  /** 편집 모달 OK — C 면 addRow, U 면 visible→actual 매핑 후 updateRow. */
  const fn_editSubmit = async () => {
    // ─── 폼 검증 + ds 반영 ─
    const values = await editForm.validateFields()
    if (editMode === 'C') {
      // C — addRow → _state='C' 마킹
      ds_list.addRow(values)
    } else {
      // U — visible→actual 인덱스 매핑 후 updateRow
      const targetId = visibleRows[editingIndex]?._rowId
      const actualIdx = ds_list.rows.findIndex((r) => r._rowId === targetId)
      if (actualIdx >= 0) ds_list.updateRow(actualIdx, values)
    }
    setEditOpen(false)
  }

  /** 삭제 — 선택 행을 일괄 deleteRow (확인 다이얼로그 후). */
  const fn_delete = () => {
    // ─── 선택 검증 + confirm + 일괄 삭제 ─
    if (!selectedRowKeys.length) {
      $msg.byId('MSG_SELECT_DELETE_ROW')
      return
    }
    $msg.confirmById('MSG_CONFIRM_DELETE', () => {
      for (const key of selectedRowKeys) {
        const idx = ds_list.rows.findIndex((r) => r._rowId === key)
        if (idx >= 0) ds_list.deleteRow(idx)
      }
      setSelectedRowKeys([])
    })
  }

  /** 저장 — 변경분(ds_list)을 inventoryDynamic/save 로 일괄 전송. */
  const fn_save = async () => {
    // ─── dirty 체크 + 벌크 저장 ─
    if (!ds_list.isDirty) {
      $msg.byId('MSG_NO_CHANGES')
      return
    }
    const res = await $transaction({
      id: 'save',
      service: 'inventoryDynamic/save',
      action: 'save',
      inds: { ds_inventory: ds_list },
      outds: { ds_inventory: ds_list }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      fn_search() // 동적 SQL 결과 재정규화 위해 재조회
    }
  }

  /** 취소 — 변경 폐기 후 재조회. */
  const fn_cancel = () => fn_search()

  /** 창고요약 — 프로시저 호출 + 키 정규화 후 ds_summary 적재. */
  const fn_summary = async () => {
    // ─── 프로시저 호출 + 키 정규화 + load ─
    const res = await $transaction({
      id: 'summary',
      service: 'inventoryDynamic/summary',
      action: 'search',
      params: searchForm.getFieldsValue()
    })
    if (res.success) {
      // 페이로드 추출 (ds_summary / ds_list / 배열 / {rows} 호환)
      const payload = res.data?.ds_summary ?? res.data?.ds_list ?? []
      const rawRows: unknown[] = Array.isArray(payload)
        ? payload
        : typeof payload === 'object' &&
            payload &&
            Array.isArray((payload as { rows?: unknown }).rows)
          ? (payload as { rows: unknown[] }).rows
          : []
      // SUMMARY 전용 매핑(WAREHOUSECD/ITEMCOUNT/TOTALQTY/LASTINBOUND) 으로 정규화
      const normalized = rawRows.map((r) => normalizeKeys<SummaryRow>(r, SUMMARY_UPPER_TO_CAMEL))
      ds_summary.load(normalized)
    }
  }

  /** 수량조정 모달 open — 단일 선택 행 정보로 dialog state 초기화. */
  const fn_openAdjust = () => {
    // ─── 단일 선택 검증 + dialog 진입 ─
    if (selectedRowKeys.length !== 1) {
      $msg.byId('MSG_SELECT_ROW')
      return
    }
    const row = visibleRows.find((r) => r._rowId === selectedRowKeys[0])
    if (!row) return
    setAdjustTarget(row.data)
    setAdjustDelta(0)
    setAdjustOpen(true)
  }

  /** 수량조정 실행 — 프로시저로 입출고 트랜잭션 (resultCode 분기). */
  const fn_adjust = async () => {
    // ─── 프로시저 호출 + resultCode 분기 ─
    if (!adjustTarget || adjustDelta === 0) return
    const res = await $transaction({
      id: 'adjust',
      service: 'inventoryDynamic/adjust',
      action: 'save',
      params: { inventoryId: adjustTarget.inventoryId, delta: adjustDelta }
    })
    if (res.success) {
      // resultCode: 0=성공 / -2=재고부족 / 그외=대상 없음
      const result = (res.data?.ds_adjustResult ?? res.data) as
        | { resultCode?: number; newQuantity?: number }
        | undefined
      const rc = result?.resultCode
      if (rc === 0) {
        $msg.success(`수량 조정 완료: ${result?.newQuantity}`)
        setAdjustOpen(false)
        fn_search()
      } else if (rc === -2) {
        $msg.byId('MSG_INVENTORY_SHORTAGE')
      } else {
        $msg.error('재고를 찾을 수 없습니다.')
      }
    }
  }

  const visibleRows = ds_list.getVisibleRows()

  const columns: ColumnsType<(typeof visibleRows)[number]> = useMemo(
    () => [
      { title: 'ID', dataIndex: ['data', 'inventoryId'], width: 80 },
      { title: t('COL_ITEM_CODE', '품목코드'), dataIndex: ['data', 'itemCode'], width: 140 },
      { title: t('COL_ITEM_NAME', '품목명'), dataIndex: ['data', 'itemName'], width: 200 },
      {
        title: t('COL_WAREHOUSE', '창고'),
        dataIndex: ['data', 'warehouseCd'],
        width: 120,
        render: (v: string) => getCodeName('WAREHOUSE', v)
      },
      {
        title: t('COL_QTY', '수량'),
        dataIndex: ['data', 'quantity'],
        width: 110,
        align: 'right',
        render: (v?: number) => v?.toLocaleString() ?? ''
      },
      {
        title: t('COL_UNIT', '단위'),
        dataIndex: ['data', 'unit'],
        width: 90,
        render: (v: string) => getCodeName('UNIT', v)
      },
      {
        title: t('COL_LAST_INBOUND', '최종입고일'),
        dataIndex: ['data', 'lastInboundDate'],
        width: 140,
        render: (v) => fmtDate(v)
      },
      { title: t('COL_NOTE', '비고'), dataIndex: ['data', 'note'] },
      {
        title: t('COL_STATUS', '상태'),
        dataIndex: '_state',
        width: 70,
        render: (s: string) => <RowStateTag state={s} updateColor="gold" />
      }
    ],
    [getCodeName, t]
  )

  const summaryColumns: ColumnsType<SummaryRow> = [
    {
      title: t('COL_WAREHOUSE', '창고'),
      dataIndex: 'warehouseCd',
      width: 120,
      render: (v: string) => getCodeName('WAREHOUSE', v)
    },
    {
      title: t('LBL_ITEM_COUNT', '품목수'),
      dataIndex: 'itemCount',
      width: 120,
      align: 'right',
      render: (v?: number) => v?.toLocaleString() ?? ''
    },
    {
      title: t('LBL_TOTAL_QTY', '총수량'),
      dataIndex: 'totalQty',
      width: 140,
      align: 'right',
      render: (v?: number) => v?.toLocaleString() ?? ''
    },
    {
      title: t('COL_LAST_INBOUND', '최종입고일'),
      dataIndex: 'lastInbound',
      width: 140,
      render: (v) => fmtDate(v)
    }
  ]

  useEffect(() => {
    // ─── 초기 로드 (mount 1회) ─
    // 라이프사이클: mount → fn_search(JDBC 동적 SQL) → 키 정규화 → ds_list 적재
    fn_search()
  }, []) // deps 비움 — 1회만 실행

  return (
    <AppPage
      search={{
        form: searchForm,
        onSearch: fn_search,
        onReset: fn_reset,
        loading,
        content: (
          <>
            <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
              <FormInput
                placeholder={t('PH_PRODUCT_NAME', '품목코드/품목명')}
                style={{ width: 180 }}
              />
            </Form.Item>
            <Form.Item name="warehouseCd" label={t('COL_WAREHOUSE', '창고')}>
              <FormSelect
                options={getOptions('WAREHOUSE')}
                style={{ minWidth: 140 }}
                placeholder={t('PH_ALL', '전체')}
              />
            </Form.Item>
            <Form.Item name="minQty" label={t('LBL_MIN_QTY', 'Min Qty')}>
              <InputNumber min={0} style={{ width: 110 }} />
            </Form.Item>
            <Form.Item name="maxQty" label={t('LBL_MAX_QTY', 'Max Qty')}>
              <InputNumber min={0} style={{ width: 110 }} />
            </Form.Item>
          </>
        )
      }}
      toolbar={
        <Flex
          justify="space-between"
          align="center"
          gap={8}
          style={{ width: '100%', flexWrap: 'nowrap', overflowX: 'auto' }}
        >
          <Space size="small" wrap={false} style={{ flexShrink: 0 }}>
            <Button onClick={fn_summary}>{t('LBL_WAREHOUSE_SUMMARY', '창고요약')}</Button>
            <Button onClick={fn_openAdjust} disabled={selectedRowKeys.length !== 1}>
              수량조정
            </Button>
          </Space>
          <CrudToolbar
            onSearch={fn_search}
            onAdd={fn_add}
            onEdit={fn_edit}
            onDelete={fn_delete}
            onSave={fn_save}
            onCancel={fn_cancel}
            loading={loading}
            dirty={ds_list.isDirty}
            style={{ width: 'auto', flexShrink: 0 }}
          />
        </Flex>
      }
    >
      <Tabs
        items={[
          {
            key: 'list',
            label: '재고목록 (SQL Append)',
            children: (
              <DataTable
                rowKey={(r) => r._rowId}
                columns={columns}
                dataSource={visibleRows}
                loading={loading}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys)
                }}
              />
            )
          },
          {
            key: 'summary',
            label: '창고요약 (Procedure)',
            children: (
              <DataTable
                rowKey={(r) => r.warehouseCd ?? ''}
                columns={summaryColumns}
                dataSource={ds_summary.getVisibleRows().map((r) => r.data)}
              />
            )
          }
        ]}
      />

      <Modal
        open={editOpen}
        title={editMode === 'C' ? '재고 추가' : '재고 수정'}
        onOk={fn_editSubmit}
        onCancel={() => setEditOpen(false)}
        okText="확인"
        cancelText="취소"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          {editMode === 'U' && (
            <Form.Item name="inventoryId" label="ID">
              <Input disabled />
            </Form.Item>
          )}
          <Form.Item
            name="itemCode"
            label={t('COL_ITEM_CODE', '품목코드')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="itemName"
            label={t('COL_ITEM_NAME', '품목명')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="warehouseCd"
            label={t('COL_WAREHOUSE', '창고')}
            rules={[{ required: true }]}
          >
            <Select options={getOptions('WAREHOUSE')} />
          </Form.Item>
          <Form.Item name="quantity" label={t('COL_QTY', '수량')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="unit" label={t('COL_UNIT', '단위')}>
            <Select options={getOptions('UNIT')} />
          </Form.Item>
          <Form.Item name="lastInboundDate" label={t('COL_LAST_INBOUND', '최종입고일')}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="note" label={t('COL_NOTE', '비고')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={adjustOpen}
        title={t('TTL_QTY_ADJUST', '수량조정')}
        onOk={fn_adjust}
        onCancel={() => setAdjustOpen(false)}
        okText={t('BTN_OK', '확인')}
        cancelText={t('BTN_CANCEL', '취소')}
      >
        {adjustTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <strong>{adjustTarget.itemName}</strong> ({adjustTarget.itemCode})
            </div>
            <div>현재수량: {adjustTarget.quantity?.toLocaleString()}</div>
            <div>
              <span style={{ marginRight: 8 }}>증감:</span>
              <InputNumber
                value={adjustDelta}
                onChange={(v) => setAdjustDelta(Number(v ?? 0))}
                style={{ width: 160 }}
              />
            </div>
            <small style={{ color: '#999' }}>양수=입고(+), 음수=출고(-)</small>
          </div>
        )}
      </Modal>
    </AppPage>
  )
}
